import { ReactElement } from "react";
import { PageContainer, PageTitle, SkeletonLoader } from "../../global/Common";
import { Box, Card, CardContent, Typography, Skeleton, Divider, useTheme, useMediaQuery, Link, Chip } from "@mui/material";
import { getStoreStatus } from "../../utils/storeHours";
import { useUserData } from "../../context/UserDataContext";
import { useLanguage } from "../../context/LanguageContext";
import { StoreMap } from "./StoreMap";
import { useBrand } from "../../context/BrandContext";

const MonMagasin = (): ReactElement => {
  const { t } = useLanguage();
  const { storeData, loading } = useUserData();
  const { theme } = useBrand();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const store = Array.isArray(storeData?.EntityResponses) 
    ? storeData?.EntityResponses[0] 
    : storeData?.EntityResponses;

  const fullAddress = store ? [
    store.AddressResponse?.address1,
    store.AddressResponse?.address2,
    store.AddressResponse?.zipCode && store.AddressResponse?.city 
      ? `${store.AddressResponse.zipCode} ${store.AddressResponse.city}`
      : store.AddressResponse?.zipCode || store.AddressResponse?.city
  ].filter(Boolean).join(', ') : '';

  const storeStatus = store?.openingHours ? getStoreStatus(store.openingHours) : null;

  return (
    <PageContainer>
      <PageTitle>{t('pages.myStore.title')}</PageTitle>
      
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, alignItems: 'stretch' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <i className="bx bx-store" style={{ fontSize: 24, color: theme.primary }} />
                {t('pages.myStore.storeInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {loading ? (
                <SkeletonLoader count={5} height={40} />
              ) : store ? (
                <Box>
                  {store.name && (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        {store.name}
                      </Typography>
                      {storeStatus && (
                        <Chip 
                          label={storeStatus.isOpen ? t('pages.myStore.open') : t('pages.myStore.closed')}
                          size="medium"
                          sx={{ 
                            bgcolor: storeStatus.isOpen ? '#4caf50' : '#f44336',
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      )}
                    </Box>
                  )}
                  
                  {(store.AddressResponse?.address1 || store.AddressResponse?.city) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <i className="bx bx-map-pin" style={{ fontSize: 16, color: theme.primary }} />
                        {t('pages.myStore.address')}
                      </Typography>
                      {store.AddressResponse?.address1 && <Typography>{store.AddressResponse.address1}</Typography>}
                      {store.AddressResponse?.address2 && <Typography>{store.AddressResponse.address2}</Typography>}
                      {(store.AddressResponse?.zipCode || store.AddressResponse?.city) && (
                        <Typography>
                          {store.AddressResponse.zipCode} {store.AddressResponse.city}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {store.PhoneResponse?.number && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <i className="bx bx-phone" style={{ fontSize: 16, color: theme.primary }} />
                        {t('pages.myStore.phone')}
                      </Typography>
                      {isMobile ? (
                        <Link 
                          href={`tel:${store.PhoneResponse.number.replace(/\s/g, '')}`}
                          sx={{ 
                            color: theme.primary,
                            textDecoration: 'underline',
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': {
                              color: theme.primary,
                              textDecoration: 'none',
                            },
                            '&:active': {
                              opacity: 0.7,
                            }
                          }}
                        >
                          {store.PhoneResponse.number}
                        </Link>
                      ) : (
                        <Typography>{store.PhoneResponse.number}</Typography>
                      )}
                    </Box>
                  )}
                  
                  {store.EmailResponse?.emailValue && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <i className="bx bx-envelope" style={{ fontSize: 16, color: theme.primary }} />
                        {t('pages.myStore.email')}
                      </Typography>
                      <Link
                        href={`mailto:${store.EmailResponse.emailValue}`}
                        sx={{
                          color: theme.primary,
                          textDecoration: 'underline',
                          fontWeight: 500,
                          cursor: 'pointer',
                          '&:hover': {
                            color: theme.primary,
                            textDecoration: 'none',
                          },
                          '&:active': {
                            opacity: 0.7,
                          }
                        }}
                      >
                        {store.EmailResponse.emailValue}
                      </Link>
                    </Box>
                  )}
                  
                  {store.openingHours && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <i className="bx bx-time" style={{ fontSize: 16, color: theme.primary }} />
                        {t('pages.myStore.openingHours')}
                      </Typography>
                      <Typography component="div">
                        {store.openingHours.split(/[,\n]+/).filter((h: string) => h.trim()).map((hour: string, index: number) => (
                          <Typography key={index} sx={{ mb: 0.5 }}>
                            {hour.trim()}
                          </Typography>
                        ))}
                      </Typography>
                    </Box>
                  )}
                  
                  {(store.latitude && store.longitude) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <i className="bx bx-navigation" style={{ fontSize: 16, color: theme.primary }} />
                        {t('pages.myStore.gpsCoordinates')}
                      </Typography>
                      <Typography>
                        {t('pages.myStore.latitude')} : {store.latitude}
                      </Typography>
                      <Typography>
                        {t('pages.myStore.longitude')} : {store.longitude}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary">
                  {t('pages.myStore.noStoreInfo')}
                </Typography>
              )}
            </CardContent>
          </Card>
          
          {loading ? (
            <Card>
              <CardContent>
                <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={300} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={40} />
              </CardContent>
            </Card>
          ) : store?.latitude && store?.longitude ? (
            <StoreMap 
              latitude={store.latitude}
              longitude={store.longitude}
              storeName={store.name}
              address={fullAddress}
            />
          ) : null}
        </Box>
      </Box>
    </PageContainer>
  );
};

export default MonMagasin;
